import { Button } from '@eventbrite/marmalade';
import { InputField } from '@eventbrite/legacy-eds.input-field';
import { gettext } from '@eventbrite/legacy-infra.i18n';
import React, { useEffect } from 'react';
import styles from './ReportEventFormFields.module.scss';
import { reportEvent } from '../../api';
import {
    INPUT_REPORT_DESCRIPTION_LABEL,
    INPUT_REPORT_EMAIL_LABEL,
    INPUT_REPORT_EMAIL_PLACEHOLDER,
    REPORT_HELPER_TEXT,
} from '../../constants';
import {
    useErrorDisplayContext,
    usePaginationDisplayContext,
    useRadioContext,
} from '../../context';
import { useForm } from '../../hooks';
import { Pages } from '../../types';
import { computeRegulationLabel } from '../../util';

export const ReportEventFormFields = (props: {
    eventId: string;
    eventName: string;
    eventUrl: string;
    country: string;
    userCountry: string;
}) => {
    const { eventId, eventName, eventUrl, country, userCountry } = props;
    const { fields, helpers } = useForm();
    const { selection } = useRadioContext();
    const { setError } = useErrorDisplayContext();
    const { setPage } = usePaginationDisplayContext();
    const emailRequired = selection === 'reason_unauthorized_event';
    const descriptionRequired = [
        'reason_unauthorized_event',
        'reason_harmful',
        'reason_canceled_event',
    ].includes(selection || '');

    useEffect(() => {
        helpers.setters.setRequired({
            email: emailRequired,
            description: descriptionRequired,
        });

        if (!fields.required.email)
            helpers.setters.setEmail({ ...fields.email, error: '' });
        if (!fields.required.description)
            helpers.setters.setDescription({
                ...fields.description,
                error: '',
            });
    }, []);

    const handleSubmit = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        let emailError = false;
        let descriptionError = false;

        if (fields.required.email) emailError = helpers.validateField('email');
        if (fields.required.description)
            descriptionError = helpers.validateField('description');

        if (!emailError && !descriptionError) {
            const regulationLabel = computeRegulationLabel(
                country,
                userCountry,
            );
            reportEvent({
                userId: fields.userId,
                email: fields.email.value || '',
                eventId,
                reportMessage: fields.description.value || '',
                reportReason: selection || 'reason_other',
                eventUrl,
                eventName,
                regulation: regulationLabel,
            })
                .then((response: Response) => response.json())
                .then((status) => {
                    if (status.message === 'success') {
                        setPage?.(Pages.success);
                    } else {
                        throw new Error('Error submitting report.');
                    }
                })
                .catch(() => {
                    setError?.(true);
                });
        }
    };

    return (
        <>
            <section className={styles.emailSection}>
                <InputField
                    id="report-email"
                    data-testid="report-email-input"
                    label={INPUT_REPORT_EMAIL_LABEL}
                    placeholder={INPUT_REPORT_EMAIL_PLACEHOLDER}
                    type="email"
                    onChange={(text: string) =>
                        helpers.setters.setEmail({
                            ...fields.email,
                            value: text,
                        })
                    }
                    value={fields.email.value}
                    hasError={!!fields.email.error}
                    required={fields.required.email}
                    style="static"
                />
                {fields.email.error && (
                    <p className={styles.errorText}>
                        {fields.email.error}
                    </p>
                )}
            </section>
            <section className={styles.descriptionSection}>
                <InputField
                    id="report-description"
                    data-testid="report-description-input"
                    label={INPUT_REPORT_DESCRIPTION_LABEL}
                    placeholder={
                        REPORT_HELPER_TEXT[selection || 'report_default']
                    }
                    onChange={(text: string) =>
                        helpers.setters.setDescription({
                            ...fields.description,
                            value: text,
                        })
                    }
                    value={fields.description.value}
                    hasError={!!fields.description.error}
                    required={fields.required.description}
                    isMultiline={true}
                    style="static"
                    type="text"
                />
                {fields.description.error && (
                    <p className={styles.errorText}>
                        {fields.description.error}
                    </p>
                )}
            </section>
            <Button
                data-testid="report-event-submit"
                onClick={handleSubmit}
                variant="primary"
                className={styles.submitButton}
            >
                {gettext('Submit Report')}
            </Button>
            <Button
                data-testid="report-event-back"
                onClick={() => {
                    setPage?.(Pages.intro);
                }}
                variant="secondary"
                className={styles.backButton}
            >
                {gettext('Go back')}
            </Button>
        </>
    );
};
